import React, { useEffect, useRef } from 'react';
import { Video, X, FileVideo, Paperclip, FileText, Trash2, Download } from 'lucide-react';
import { toast } from 'react-toastify';

const CurriculumPreviewModal = ({ 
  videoToPreview, 
  onClose, 
  getFullVideoUrl, 
  handleDeleteVariant 
}) => {
  const videoRef = useRef(null);
  


  useEffect(() => {
    if (videoToPreview?.videoUrl && videoRef.current) {
      // Small timeout to ensure the DOM is ready and the src is set
      const playTimeout = setTimeout(() => {
        videoRef.current.play().catch(err => {
          console.log("Autoplay failed or was prevented:", err);
        });
      }, 150);
      return () => clearTimeout(playTimeout);
    }
  }, [videoToPreview, videoToPreview?.videoUrl]);

  if (!videoToPreview) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 md:p-8 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative w-full max-w-5xl bg-white rounded-[2.5rem] overflow-hidden border border-orange-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 bg-white text-black z-10">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center border border-white/30">
              <Video className="text-black" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-black leading-tight mb-0.5">{videoToPreview.title}</h3>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-black cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Split Content Area */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-slate-50/50">
          {/* Left Column: Video Player */}
          <div className="flex-1 p-6 md:p-10 flex items-center justify-center bg-slate-50 border-r border-orange-50">
            <div className="w-full aspect-video rounded-3xl overflow-hidden bg-black border border-orange-100/50 relative group">
              {videoToPreview.videoUrl ? (
                <video 
                  ref={videoRef}
                  key={videoToPreview.videoUrl}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className="w-full h-full object-contain"
                  src={getFullVideoUrl(videoToPreview.videoUrl)}
                  onError={(e) => {
                    console.error("Video failed to load:", e.target.src);
                    toast.error("Video could not be loaded. Please check the file path.");
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="flex flex-col items-center justify-center text-orange-200 gap-6 py-20">
                  <div className="w-24 h-24 rounded-full bg-orange-50 flex items-center justify-center border-4 border-orange-100 animate-pulse">
                    <FileVideo size={48} className="text-orange-300" />
                  </div>
                  <div className="text-center">
                    <h4 className="text-slate-800 text-lg font-bold mb-1">Document Only Module</h4>
                    <p className="text-sm text-slate-500">Review the attached materials on the right</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Attachments Sidebar */}
          <div className="w-full md:w-[350px] bg-white flex flex-col border-l border-orange-50">
            <div className="p-6 border-b border-orange-50 bg-orange-50/20">
              <h4 className="text-sm font-bold text-slate-800 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-orange-500 text-white flex items-center justify-center">
                  <Paperclip size={14} />
                </span>
                Attachments
              </h4>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 tracking-wider">
                {videoToPreview.variants?.length || 0} Total Attachments
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {videoToPreview.variants && videoToPreview.variants.length > 0 ? (
                videoToPreview.variants.map((variant, vIdx) => {
                  const rawPath = variant.variant_url || variant.url || variant.path || variant.fileurl || variant.attachment_url;
                  const fileName = variant.name || variant.variant_name || rawPath || 'Attachment';
                  let displayLabel = fileName.split('/').pop().split('\\').pop();
                  const ext = (variant.filetype || variant.type || '').toLowerCase();
                  if (ext && !displayLabel.toLowerCase().endsWith(`.${ext}`)) {
                    displayLabel = `${displayLabel}.${ext}`;
                  }
                  const isPDF = ext === 'pdf' || /\.pdf$/i.test(displayLabel);
                  
                  const cleanRaw = String(rawPath || '').replace(/\\/g, "/").replace(/^\/+/, '');
                  const fileNameWithExt = ext && !cleanRaw.toLowerCase().endsWith(`.${ext}`) ? `${cleanRaw}.${ext}` : cleanRaw;
                  const variantUrl = getFullVideoUrl(fileNameWithExt);
                  
                  return (
                    <div
                      key={vIdx}
                      className="group relative flex flex-col p-4 bg-slate-50 hover:bg-orange-50 border border-slate-100 hover:border-orange-200 rounded-2xl transition-all duration-300"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${
                          isPDF ? 'bg-red-50 text-red-500' : 'bg-orange-100 text-orange-600'
                        }`}>
                          {isPDF ? <FileText size={18} /> : <Paperclip size={18} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-700 truncate group-hover:text-orange-600 mb-0.5" title={displayLabel}>
                            {displayLabel}
                          </p>
                          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">
                            {isPDF ? 'PDF Document' : 'Media Asset'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 pt-3 border-t border-slate-200/50">
                        <a 
                          href={variantUrl || '#'}
                          download={displayLabel || 'download'}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!variantUrl) {
                              e.preventDefault();
                              toast.error("Download link not available");
                            }
                          }}
                          className="flex-1 py-1.5 bg-white group-hover:bg-orange-500 text-slate-600 group-hover:text-white border border-slate-200 group-hover:border-orange-500 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-2 cursor-pointer no-underline"
                        >
                          <Download size={12} /> Download
                        </a>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVariant(variant, videoToPreview);
                          }}
                          className="w-9 py-1.5 bg-white hover:bg-red-50 text-slate-300 hover:text-red-500 border border-slate-200 hover:border-red-100 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center cursor-pointer"
                          title="Delete Resource"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-200 mb-4 border-2 border-dashed border-slate-100">
                    <Paperclip size={24} />
                  </div>
                  <h5 className="text-xs font-bold text-slate-400">No Attachments</h5>
                  <p className="text-[10px] text-slate-300 mt-1">Supporting files will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurriculumPreviewModal;